'use client';

import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

type TourState = {
  isRunning: boolean;
  currentStepIndex: number;
  isDismissed: boolean;
};

type TourContextValue = {
  getState: (tourKey: string) => TourState;
  start: (tourKey: string, options?: { startAt?: number }) => void;
  stop: (tourKey: string) => void;
  setStep: (tourKey: string, index: number) => void;
  next: (tourKey: string) => void;
  prev: (tourKey: string) => void;
  dismiss: (tourKey: string) => void;
  reset: (tourKey: string) => void;
};

const TourContext = createContext<TourContextValue | undefined>(undefined);

type ToursMap = Record<string, TourState>;

export function TourProvider({ children }: { children: ReactNode }) {
  const [tours, setTours] = useState<ToursMap>({});

  const getState = useCallback(
    (tourKey: string): TourState => {
      return tours[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false };
    },
    [tours]
  );

  const start = useCallback((tourKey: string, options?: { startAt?: number }) => {
    setTours((prev) => ({
      ...prev,
      [tourKey]: {
        isRunning: true,
        currentStepIndex: options?.startAt ?? 0,
        isDismissed: false
      }
    }));
  }, []);

  const stop = useCallback((tourKey: string) => {
    setTours((prev) => {
      const current = prev[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false };
      return {
        ...prev,
        [tourKey]: {
          isRunning: false,
          currentStepIndex: current.currentStepIndex,
          isDismissed: current.isDismissed
        }
      };
    });
  }, []);

  const setStep = useCallback((tourKey: string, index: number) => {
    setTours((prev) => {
      const current = prev[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false };
      if (current.currentStepIndex === index) return prev;
      return {
        ...prev,
        [tourKey]: {
          ...current,
          currentStepIndex: index
        }
      };
    });
  }, []);

  const next = useCallback((tourKey: string) => {
    setTours((prev) => ({
      ...prev,
      [tourKey]: {
        ...(prev[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false }),
        currentStepIndex: (prev[tourKey]?.currentStepIndex ?? 0) + 1
      }
    }));
  }, []);

  const prev = useCallback((tourKey: string) => {
    setTours((prev) => ({
      ...prev,
      [tourKey]: {
        ...(prev[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false }),
        currentStepIndex: Math.max(0, (prev[tourKey]?.currentStepIndex ?? 0) - 1)
      }
    }));
  }, []);

  const dismiss = useCallback((tourKey: string) => {
    setTours((prev) => {
      const current = prev[tourKey] ?? { isRunning: false, currentStepIndex: 0, isDismissed: false };
      return {
        ...prev,
        [tourKey]: {
          ...current,
          isRunning: false,
          isDismissed: true
        }
      };
    });
  }, []);

  const reset = useCallback((tourKey: string) => {
    setTours((prev) => ({
      ...prev,
      [tourKey]: {
        isRunning: false,
        currentStepIndex: 0,
        isDismissed: false
      }
    }));
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({ getState, start, stop, setStep, next, prev, dismiss, reset }),
    [getState, next, prev, setStep, start, stop, dismiss, reset]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue;
export function useTour(tourKey: string): TourContextValue & {
  state: TourState;
  isRunning: boolean;
  currentStepIndex: number;
  isDismissed: boolean;
  bound: {
    start: (options?: { startAt?: number }) => void;
    stop: () => void;
    setStep: (index: number) => void;
    next: () => void;
    prev: () => void;
    dismiss: () => void;
    reset: () => void;
  };
};
export function useTour(tourKey?: string) {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  const state = tourKey ? ctx.getState(tourKey) : undefined;

  const bound = useMemo(
    () => ({
      start: (options?: { startAt?: number }) => (tourKey ? ctx.start(tourKey, options) : undefined),
      stop: () => (tourKey ? ctx.stop(tourKey) : undefined),
      setStep: (index: number) => (tourKey ? ctx.setStep(tourKey, index) : undefined),
      next: () => (tourKey ? ctx.next(tourKey) : undefined),
      prev: () => (tourKey ? ctx.prev(tourKey) : undefined),
      dismiss: () => (tourKey ? ctx.dismiss(tourKey) : undefined),
      reset: () => (tourKey ? ctx.reset(tourKey) : undefined)
    }),
    [ctx, tourKey]
  );

  if (!tourKey) return ctx;

  return {
    ...ctx,
    state: state as TourState,
    isRunning: (state as TourState).isRunning,
    currentStepIndex: (state as TourState).currentStepIndex,
    isDismissed: (state as TourState).isDismissed,
    bound
  } as const;
}
